import React, { useEffect } from 'react';
import toast, { Toaster } from 'react-hot-toast';

// Hub-wide toast surface.
// Vanilla-JS pages (limits.js, scheduled-tasks.js, inline scripts in
// gallery/files/document/etc.) call `window.hubToast.success(...)` and friends.
// The React mount lives once at <Toaster /> from this file, attached in main.jsx.

const HUB_TOAST_STYLE = {
  background: '#1a0e0e',
  color: '#f4ecea',
  border: '1px solid rgba(255, 110, 110, 0.35)',
  borderRadius: '10px',
  padding: '10px 14px',
  fontSize: '13px',
  fontWeight: 500,
  fontFamily: 'ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
  boxShadow: '0 12px 32px rgba(0, 0, 0, 0.45)',
  maxWidth: '480px',
};

const HUB_TOAST_SUCCESS = {
  iconTheme: { primary: '#4be07a', secondary: '#1a0e0e' },
  style: { ...HUB_TOAST_STYLE, borderColor: 'rgba(75, 224, 122, 0.45)' },
};

const HUB_TOAST_ERROR = {
  iconTheme: { primary: '#ff5a5a', secondary: '#1a0e0e' },
  style: { ...HUB_TOAST_STYLE, borderColor: 'rgba(255, 90, 90, 0.55)' },
  duration: 5_000,
};

const HUB_TOAST_LOADING = {
  iconTheme: { primary: '#f3c98d', secondary: '#1a0e0e' },
  style: { ...HUB_TOAST_STYLE, borderColor: 'rgba(243, 201, 141, 0.45)' },
};

// Map an old (msg, kind) call from the legacy showToast helpers into the
// right react-hot-toast variant.
function toastByKind(msg, kind) {
  const m = String(msg == null ? '' : msg);
  switch (kind) {
    case 'error':
    case 'danger':
      return toast.error(m, HUB_TOAST_ERROR);
    case 'warning':
    case 'warn':
      return toast(m, { icon: '⚠️', style: { ...HUB_TOAST_STYLE, borderColor: 'rgba(243, 201, 141, 0.55)' }, duration: 4_500 });
    case 'info':
      return toast(m, { icon: 'ℹ️', style: HUB_TOAST_STYLE });
    case 'loading':
      return toast.loading(m, HUB_TOAST_LOADING);
    case 'success':
    default:
      return toast.success(m, HUB_TOAST_SUCCESS);
  }
}

export function HubToastMount() {
  // Expose a tiny global so vanilla-JS pages can fire toasts without
  // needing to import anything. The shape mirrors `toast` from react-hot-toast.
  useEffect(() => {
    const api = {
      success: (msg, opts) => toast.success(String(msg ?? ''), { ...HUB_TOAST_SUCCESS, ...(opts || {}) }),
      error:   (msg, opts) => toast.error(String(msg ?? ''),   { ...HUB_TOAST_ERROR,   ...(opts || {}) }),
      loading: (msg, opts) => toast.loading(String(msg ?? ''), { ...HUB_TOAST_LOADING, ...(opts || {}) }),
      info:    (msg, opts) => toast(String(msg ?? ''), { icon: 'ℹ️', style: HUB_TOAST_STYLE, ...(opts || {}) }),
      warning: (msg, opts) => toast(String(msg ?? ''), { icon: '⚠️', style: { ...HUB_TOAST_STYLE, borderColor: 'rgba(243, 201, 141, 0.55)' }, duration: 4_500, ...(opts || {}) }),
      custom:  (msg, opts) => toast(String(msg ?? ''), { style: HUB_TOAST_STYLE, ...(opts || {}) }),
      // Legacy adapter: existing pages that called showToast(msg, kind) can
      // now call window.hubToast.show(msg, kind) verbatim.
      show:    (msg, kind) => toastByKind(msg, kind),
      promise: (p, msgs, opts) => toast.promise(p, msgs, { style: HUB_TOAST_STYLE, ...(opts || {}) }),
      dismiss: (id) => toast.dismiss(id),
      remove:  (id) => toast.remove(id),
    };
    try { window.hubToast = api; } catch {}
    return () => {
      try { if (window.hubToast === api) delete window.hubToast; } catch {}
    };
  }, []);

  return (
    <Toaster
      position="top-center"
      gutter={8}
      containerStyle={{ zIndex: 100000 }}
      toastOptions={{
        duration: 3_500,
        style: HUB_TOAST_STYLE,
        success: HUB_TOAST_SUCCESS,
        error: HUB_TOAST_ERROR,
        loading: HUB_TOAST_LOADING,
      }}
    />
  );
}
